export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
}

export interface ConversationHistory {
  messages: ConversationMessage[]
  sessionId: string
  createdAt: number
}

export interface AskRequest {
  question: string
  pageContext: PageContext
  conversationHistory?: ConversationMessage[]
}

export interface AskResponse {
  answer: string
  error?: string
  conversationId?: string
}

export interface PageContext {
  title: string
  url: string
  content: string
  businessInfo: {
    name: string
    description: string
    features: string[]
    benefits: string[]
  }
}

// OpenAI message format for API calls
export interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

// Utility functions for conversation management
export const CONVERSATION_MEMORY_LIMIT = 5 // 5 Q&A pairs = 10 messages max

export function createConversationMessage(
  role: 'user' | 'assistant',
  content: string
): ConversationMessage {
  return {
    id: generateMessageId(),
    role,
    content,
    timestamp: Date.now(),
  }
}

export function addToConversationHistory(
  history: ConversationMessage[],
  message: ConversationMessage
): ConversationMessage[] {
  const newHistory = [...history, message]

  // Keep only the last 10 messages (5 Q&A pairs)
  if (newHistory.length > CONVERSATION_MEMORY_LIMIT * 2) {
    return newHistory.slice(-CONVERSATION_MEMORY_LIMIT * 2)
  }

  return newHistory
}

export function buildOpenAIMessages(
  systemPrompt: string,
  conversationHistory: ConversationMessage[],
  currentQuestion: string,
  pageContext: PageContext
): OpenAIMessage[] {
  const messages: OpenAIMessage[] = [
    {
      role: 'system',
      content: systemPrompt,
    },
  ]

  // Add conversation history
  conversationHistory.forEach((msg) => {
    messages.push({
      role: msg.role,
      content: msg.content,
    })
  })

  // Add current question with page context
  const contextualPrompt = `
Page Context:
- Page Title: ${pageContext.title}
- URL: ${pageContext.url}
- Business: ${pageContext.businessInfo.name}
- Service Description: ${pageContext.businessInfo.description}
- Key Features: ${pageContext.businessInfo.features.join(', ')}
- Benefits: ${pageContext.businessInfo.benefits.join(', ')}

Page Content:
${pageContext.content}

User Question: ${currentQuestion}

Please answer the user's question using the above context about the Ask Anything service and our conversation history.`

  messages.push({
    role: 'user',
    content: contextualPrompt,
  })

  return messages
}

function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
